import { FC, useEffect, useState, useRef } from 'react';

import {
  MDXEditor,
  MDXEditorMethods,
  type MDXEditorProps,
} from '@mdxeditor/editor';
import cn from 'clsx';
import { getNestedProperty } from 'common/utils';
import { Controller, useFormContext } from 'react-hook-form';

import '@mdxeditor/editor/style.css';

import styles from './mdxeditor.module.scss';

export { emptyParagraphPlugin } from './emptyParagraph/emptyParagraphPlugin';
export { textAlignmentPlugin } from './textAlignment/textAlignmentPlugin';
export { TextAlignmentToggle } from './textAlignment/TextAlignmentToggle';

type IFormMDXEditorProps = {
  name: string;
  label?: string;
  labelPosition?: 'top' | 'left';
  required?: boolean;
  className?: string;
} & Omit<MDXEditorProps, 'markdown'>;

export const FormMDXEditor: FC<IFormMDXEditorProps> = ({
  name,
  label,
  labelPosition = 'top',
  required,
  className,
  ...props
}) => {
  const [editorState, setEditorState] = useState('');
  const editorRef = useRef<MDXEditorMethods>(null);

  const { control, formState } = useFormContext();

  useEffect(() => {
    const defaultValue = getNestedProperty(
      formState.defaultValues ?? {},
      name.replace(/[[]/g, '.').replace(/[\]]/g, '').split('.'),
    );

    if (defaultValue && editorRef) {
      setEditorState(defaultValue);
      editorRef.current?.setMarkdown(defaultValue);
    }
  }, []);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { name, onChange }, fieldState: { error } }) => {
        return (
          <div
            className={cn(
              styles.container,
              styles.inputWrapper,
              styles[`label-${labelPosition}`],
              className,
            )}
          >
            {!!label && (
              <label htmlFor={name} className={styles.label}>
                {label}
                {required && <span className={styles.required}>*</span>}
              </label>
            )}
            <MDXEditor
              className={styles.editorContainer}
              markdown={editorState}
              onChange={(markdown) => {
                setEditorState(markdown);
                onChange(markdown);
              }}
              ref={editorRef}
              {...props}
            />
            {!!error && <div className={styles.error}>{error?.message}</div>}
          </div>
        );
      }}
    />
  );
};
